import { useMemo, useEffect } from "react";
import { useApp } from "../components/AppContext";
import { MainLayout } from "../components/MainLayout";
import {
	useAdvancedTaskSorting,
	useTaskStats,
	Task,
} from "../hooks/useTaskSorting";
import { useGetExtra } from "../api/extras/useGetExtra";
import { useCurrentUser } from "../api/auth/useCurrentUser";
import { useTranslation } from "../i18n/TranslationContext";
import { useAppRouter } from "@/hooks/useAppRouter";

export function ProfileCompletionOverview() {
	const { t } = useTranslation();
	const {
		navigateWithProcessCheck,
		navigateWithReturnTo,
		navigateDirectly,
		updateOnboardingState,
	} = useApp();

	const router = useAppRouter();

	// Get current user to fetch their ID
	const { user } = useCurrentUser();

	// Use the hook to fetch user data with dynamic ID
	const { extra, loading: isLoading, refetch } = useGetExtra(user?.extraId);

	// Refetch extra data when component mounts to ensure fresh data
	// Also update onboarding state to prevent redirect to onboarding welcome
	useEffect(() => {
		if (user?.extraId) {
			refetch();
		}
		// Set hasCompletedOnboarding to true when user reaches this screen
		updateOnboardingState({
			hasCompletedOnboarding: true,
			isFirstTimeUser: false,
		});
	}, [user?.extraId, refetch]);

	// Check if profile is complete based on backend profile_status
	const isProfileComplete = useMemo(() => {
		return extra?.profile_status === "ready_to_work";
	}, [extra]);

	// Check if basic profile is complete but missing Airport ID
	const isBaseProfileComplete = useMemo(() => {
		return extra?.profile_status === "base_profile_completed";
	}, [extra]);

	// Check if code of conduct is accepted based on backend data
	const codeOfConductAccepted = useMemo(() => {
		return (
			extra?.code_of_conduct !== null && extra?.code_of_conduct !== undefined
		);
	}, [extra]);

	// Check if payroll data is complete (only for external extras)
	const isPayrollDataComplete = useMemo(() => {
		// Internal extras don't need payroll data
		if (user?.internal_extra === true) {
			return true;
		}

		// External extras need to fill: iban, bic, gender, insurance_type
		// AND either sozialversicherung OR (city_of_birth, country_of_birth, date_of_birth, geburtsname)
		const hasBasicPayroll = !!(
			extra?.iban &&
			extra?.bic &&
			extra?.gender &&
			extra?.insurance_type
		);

		const hasSozialversicherung = !!extra?.sozialversicherung;
		const hasBirthData = !!(
			extra?.city_of_birth &&
			extra?.country_of_birth &&
			extra?.date_of_birth &&
			extra?.geburtsname
		);

		return hasBasicPayroll && (hasSozialversicherung || hasBirthData);
	}, [extra, user?.internal_extra]);

	// Calculate profile progress
	const profileProgress = useMemo(() => {
		// For internal extras: 2 tasks (Profile, Code of Conduct)
		// For external extras: 3 tasks (Profile, Payroll Data, Code of Conduct)
		const totalTasks = user?.internal_extra === true ? 2 : 3;
		let completedTasks = 0;

		if (isProfileComplete) completedTasks++;
		// Only count payroll data for external extras
		if (user?.internal_extra !== true && isPayrollDataComplete)
			completedTasks++;
		if (codeOfConductAccepted) completedTasks++;

		const percentage = Math.round((completedTasks / totalTasks) * 100);

		return {
			completed: completedTasks,
			total: totalTasks,
			percentage,
		};
	}, [
		isProfileComplete,
		isPayrollDataComplete,
		codeOfConductAccepted,
		user?.internal_extra,
	]);

	const profileTasks: Task[] = [
		{
			id: "profile",
			title: isProfileComplete
				? t("profileCompletionOverview.profile")
				: t("profileCompletionOverview.completeYourProfile"),
			description: isBaseProfileComplete
				? t("profileCompletionOverview.airportIdRequired")
				: isProfileComplete
					? t("profileCompletionOverview.yourProfileInformation")
					: t("profileCompletionOverview.fillPersonalInformation"),
			completed: isProfileComplete,
			screen: "app-profile-completion" as const,
			order: 1,
			isRequired: true,
		},
		// Only show payroll data task for external extras
		...(user?.internal_extra !== true
			? ([
					{
						id: "payroll-data",
						title: isPayrollDataComplete
							? t("profileCompletionOverview.payrollData")
							: t("profileCompletionOverview.completePayrollData"),
						description: isPayrollDataComplete
							? t("profileCompletionOverview.payrollDataComplete")
							: t("profileCompletionOverview.fillPayrollInformation"),
						completed: isPayrollDataComplete,
						screen: "app-payroll-data" as const,
						order: 2,
						isRequired: true,
					},
				] as Task[])
			: []),
		// Only show PRM data task for PRM extras
		...(extra?.is_prm === true
			? ([
					{
						id: "prm-data",
						title: "Angaben zu Ihrer Begleitpersonen (optional)",
						description: "PRM-Informationen und Assistentendaten verwalten",
						completed: true,
						screen: "app-prm-data" as const,
						order: user?.internal_extra === true ? 2 : 3,
						isRequired: false,
					},
				] as Task[])
			: []),
		{
			id: "code-of-conduct",
			title: codeOfConductAccepted
				? t("profileCompletionOverview.codeOfConduct")
				: t("profileCompletionOverview.agreeToCodeOfConduct"),
			description: codeOfConductAccepted
				? t("profileCompletionOverview.termsAccepted")
				: t("profileCompletionOverview.reviewAndAcceptTerms"),
			completed: codeOfConductAccepted,
			screen: "app-code-of-conduct" as const,
			order: user?.internal_extra === true ? 2 : 3,
			isRequired: true,
		},
	];

	// Use advanced task sorting hook
	const sortedTasks = useAdvancedTaskSorting(profileTasks);

	// Get task statistics
	const taskStats = useTaskStats(profileTasks);

	const handleTaskClick = (screen: string) => {
		// Use navigateWithReturnTo to ensure tasks return to ProfileCompletionOverview
		navigateWithReturnTo(screen as any, "app-profile-completion-overview");
	};

	// Show loading state
	if (isLoading) {
		return (
			<MainLayout showBack={true} onBack={() => navigateDirectly("dashboard")}>
				<div className="flex items-center justify-center min-h-screen">
					<div className="text-center">
						<div className="w-12 h-12 border-4 border-[var(--ucm-primary-teal)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
						<p className="text-[var(--ucm-text-medium)]">
							{t("profileCompletionOverview.loadingProfileData")}
						</p>
					</div>
				</div>
			</MainLayout>
		);
	}

	return (
		<MainLayout showBack={true} onBack={() => router.replace("dashboard")}>
			<div className="profile-completion-container bg-[var(--ucm-bg-gray)] min-h-screen pb-6">
				{/* Header */}

				{/* Progress Summary */}
				<div className="profile-progress-summary bg-white mx-4 rounded-2xl p-6 mb-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
					<div className="flex items-center space-x-4">
						{/* Progress Circle */}
						<div className="relative w-20 h-20 flex-shrink-0">
							<div className="w-20 h-20 rounded-full border-4 border-[var(--ucm-border-light)] relative overflow-hidden">
								{profileProgress.percentage === 100 ? (
									<div className="absolute inset-0 rounded-full border-4 border-[var(--ucm-primary-teal)]"></div>
								) : (
									<div
										className="absolute inset-0 rounded-full border-4 border-[var(--ucm-primary-teal)] border-l-transparent border-b-transparent"
										style={{
											transform: `rotate(${profileProgress.percentage * 3.6 - 90}deg)`,
											transition: "transform 0.3s ease",
										}}
									></div>
								)}
								<div className="absolute inset-0 flex items-center justify-center">
									<span className="text-lg font-bold text-[var(--ucm-primary-teal)]">
										{profileProgress.percentage}%
									</span>
								</div>
							</div>
						</div>

						{/* Progress Info */}
						<div className="flex-1">
							<h3 className="text-lg font-semibold text-[var(--ucm-text-dark)] mb-1">
								{t("profileCompletionOverview.sectionsCompleted", {
									completed: profileProgress.completed,
									total: profileProgress.total,
								})}
							</h3>
							<p className="text-sm text-[var(--ucm-text-medium)]">
								{t("profileCompletionOverview.completeAllSections")}
							</p>
						</div>
					</div>
				</div>

				{/* Profile Tasks List - Automatically Sorted */}
				<div className="profile-tasks-list px-4 space-y-3">
					{sortedTasks.map((task, index) => {
						const isTopIncomplete = index === 0 && !task.completed;

						return (
							<button
								key={task.id}
								onClick={() => handleTaskClick(task.screen)}
								className={`profile-task-item w-full bg-white rounded-2xl pl-4 pr-0 py-4 shadow-[0_4px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.12)] transition-all duration-200 text-left ${
									!task.isRequired
										? "border-l-4 border-black"
										: task.completed
											? "border-l-4 border-[var(--ucm-success-green)] opacity-90"
											: task.id === "profile" && isBaseProfileComplete
												? "border-l-4 border-[var(--ucm-warning-yellow)]"
												: "border-l-4 border-[var(--ucm-warning-orange)]"
								} ${
									isTopIncomplete
										? "ring-2 ring-[var(--ucm-warning-orange)] ring-opacity-20 shadow-[0_6px_20px_rgba(245,158,11,0.15)]"
										: ""
								}`}
							>
								<div className="items-center flex w-full justify-between mr-5">
									{/* Task Status Icon */}

									{/* Task Details */}
									<div className="task-details flex-1">
										<h4 className="font-semibold text-[var(--ucm-text-dark)]">
											{task.title}
											{isTopIncomplete && (
												<span className="ml-2 text-xs font-medium text-[var(--ucm-warning-orange)] bg-[var(--ucm-light-orange)] px-2 py-1 rounded-full">
													{t("profileCompletionOverview.next")}
												</span>
											)}
										</h4>
									</div>

									{/* Task Arrow */}
									<div className="task-arrow">
										<svg
											width="16"
											height="16"
											viewBox="0 0 16 16"
											fill="none"
											className="text-[var(--ucm-text-light)]"
										>
											<path
												d="M6 12L10 8L6 4"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</div>
								</div>
							</button>
						);
					})}
				</div>

				{/* Enhanced Help Message with Task Stats */}
				{taskStats.incomplete > 0 && (
					<div className="mx-4 mt-6">
						<div className="bg-[var(--ucm-light-blue)] border border-[var(--ucm-info-blue)] rounded-2xl p-4">
							<div className="flex items-start space-x-3">
								<span className="text-xl">💡</span>
								<div>
									<h3 className="font-semibold text-[var(--ucm-dark-blue)] mb-1">
										{t("profileCompletionOverview.completeAtYourOwnPace")}
									</h3>
									<p className="text-sm text-[var(--ucm-dark-blue)] mb-2">
										{t("profileCompletionOverview.completeTasksInAnyOrder")}
									</p>
									{taskStats.nextTask && (
										<p className="text-xs text-[var(--ucm-dark-blue)] bg-white bg-opacity-50 px-2 py-1 rounded-full inline-block">
											{t("profileCompletionOverview.nextSuggested")}{" "}
											<span className="font-medium">
												{taskStats.nextTask.title}
											</span>
										</p>
									)}
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Completion Celebration */}
				{taskStats.isFullyComplete && (
					<div className="mx-4 mt-6">
						<div className="bg-[var(--ucm-light-green)] border border-[var(--ucm-success-green)] rounded-2xl p-4">
							<div className="flex items-start space-x-3">
								<span className="text-xl">🎉</span>
								<div className="flex-1">
									<h3 className="font-semibold text-[var(--ucm-dark-green)] mb-1">
										{t("profileCompletionOverview.profileCompleted")}
									</h3>
									<p
										className="text-sm text-[var(--ucm-dark-green)] mb-3"
										dangerouslySetInnerHTML={{
											__html: t("profileCompletionOverview.congratulationsMessage"),
										}}
									/>
									<button
										onClick={() => navigateDirectly("app-event-application")}
										className="w-full bg-[var(--ucm-success-green)] text-white px-4 py-3 rounded-xl font-semibold hover:bg-[var(--ucm-dark-green)] transition-colors duration-200 shadow-md hover:shadow-lg"
									>
										Terminvergabe für den Probebetrieb
									</button>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		</MainLayout>
	);
}
